import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { Toast } from './Toast';
import { registerAccount, signIn } from '../lib/platform-api';

type AuthMode = 'login' | 'register';

type AuthPageProps = {
  mode: AuthMode;
  onNavigate: (path: '/login' | '/register') => void;
};

type AuthIconName =
  | 'app-window'
  | 'chart-network'
  | 'circle-x-dark'
  | 'circle-x-light'
  | 'eye'
  | 'eye-off'
  | 'github'
  | 'youtube';

function AuthIcon({ name, className = 'size-[18px]' }: { name: AuthIconName; className?: string }) {
  const common = { fill: 'none', xmlns: 'http://www.w3.org/2000/svg', className, 'aria-hidden': true } as const;

  if (name === 'chart-network') {
    return (
      <svg {...common} viewBox="0 0 22 22">
        <path d="M12.017 7.025 13.649 9.475M12.982 11.722 9.935 13.028M18.333 3.667l-5.555 1.388M2.75 2.75v14.667c0 .486.193.952.537 1.296.344.344.81.537 1.296.537H19.25M12.833 5.5A1.833 1.833 0 1 1 9.167 5.5a1.833 1.833 0 0 1 3.666 0ZM16.5 11a1.833 1.833 0 1 1-3.667 0 1.833 1.833 0 0 1 3.667 0ZM10.083 13.75a1.833 1.833 0 1 1-3.666 0 1.833 1.833 0 0 1 3.666 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'github') {
    return (
      <svg {...common} viewBox="0 0 18 18">
        <path d="M6.75 16.5v-3c-.053-.465-.015-.937.112-1.387.128-.45.346-.87.638-1.238-2.25 0-4.5-1.5-4.5-4.125a3.84 3.84 0 0 1 .75-2.625 5.24 5.24 0 0 1 0-2.626S4.5 1.5 6 2.624a16.16 16.16 0 0 1 6 0C13.5 1.5 14.25 1.5 14.25 1.5a5.4 5.4 0 0 1 0 2.626A3.84 3.84 0 0 1 15 6.75c0 2.625-2.25 4.125-4.5 4.125.585.743.854 1.686.75 2.625v3M6.75 13.5C3.367 15 3 12 1.5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'youtube') {
    return (
      <svg {...common} viewBox="0 0 18 18">
        <path d="M1.874 5.25a18 18 0 0 0 0 7.5c.069.25.202.479.386.663.184.184.413.317.664.386a37.1 37.1 0 0 0 12.15 0c.251-.069.48-.202.664-.386.184-.184.317-.413.386-.664a18 18 0 0 0 0-7.499 1.5 1.5 0 0 0-1.05-1.05 37.1 37.1 0 0 0-12.15 0 1.5 1.5 0 0 0-1.05 1.05Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'app-window') {
    return (
      <svg {...common} viewBox="0 0 18 18">
        <path d="M7.49988 3.0006V6.0003M1.4994 6.0003H16.5006M4.49964 3.0006V6.0003M2.99952 3.0006H15.0005C15.829 3.0006 16.5006 3.67211 16.5006 4.50045V13.4996C16.5006 14.3279 15.829 14.9994 15.0005 14.9994H2.99952C2.17103 14.9994 1.4994 14.3279 1.4994 13.4996V4.50045C1.4994 3.67211 2.17103 3.0006 2.99952 3.0006Z" stroke="#000" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'eye') {
    return (
      <svg {...common} viewBox="0 0 20 20">
        <path d="M1.718 9.71a.83.83 0 0 0 0 .581 8.96 8.96 0 0 0 16.563 0 .83.83 0 0 0 0-.58 8.96 8.96 0 0 0-16.563 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === 'eye-off') {
    return (
      <svg {...common} viewBox="0 0 18 18">
        <path d="M8.05 3.807a8.23 8.23 0 0 1 8.404 4.932.75.75 0 0 1 0 .522 8.3 8.3 0 0 1-1.083 1.867m-4.808-.51a2.25 2.25 0 0 1-3.181-3.181m5.727 5.687A8.23 8.23 0 0 1 1.546 9.261a.75.75 0 0 1 0-.522 8.23 8.23 0 0 1 3.335-3.858M1.5 1.5l15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  const dark = name === 'circle-x-dark';
  return (
    <svg {...common} viewBox="0 0 18 18">
      <path d="M11.2502 6.74982L6.74982 11.2502M6.74982 6.74982L11.2502 11.2502M16.5006 9C16.5006 13.1425 13.1425 16.5006 9 16.5006C4.85753 16.5006 1.4994 13.1425 1.4994 9C1.4994 4.85753 4.85753 1.4994 9 1.4994C13.1425 1.4994 16.5006 4.85753 16.5006 9Z" stroke={dark ? '#09090B' : 'currentColor'} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function BrandMark({ mode }: { mode: AuthMode }) {
  return (
    <span className={`auth-brand-mark ${mode === 'register' ? 'auth-brand-mark-register' : ''}`}>
      {mode === 'login' ? <AuthIcon name="chart-network" className="size-[22px]" /> : <span className="auth-diamond" />}
    </span>
  );
}

function SocialButton({ icon, children, light = false, onClick }: { icon: AuthIconName; children: ReactNode; light?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`auth-social-button interactive ${light ? 'auth-social-button-light' : ''}`} onClick={onClick}>
      <AuthIcon name={icon} />
      <span>{children}</span>
    </button>
  );
}

function PasswordField({ id, label, hint, value, visible, onChange, onToggle }: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  visible: boolean;
  onChange: (value: string) => void;
  onToggle: () => void;
}) {
  return (
    <div className={`auth-field ${hint ? 'auth-field-with-hint' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <span className="auth-password-control">
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="••••••••"
          required
          minLength={8}
          pattern={hint ? '(?=.*[A-Z])(?=.*[0-9]).{8,}' : undefined}
        />
        <button type="button" onClick={onToggle} aria-label={visible ? `Sembunyikan ${label.toLowerCase()}` : `Tampilkan ${label.toLowerCase()}`}>
          <AuthIcon name={visible ? 'eye-off' : 'eye'} className="size-5" />
        </button>
      </span>
      {hint && <small>{hint}</small>}
    </div>
  );
}

export function AuthPage({ mode, onNavigate }: AuthPageProps) {
  const register = mode === 'register';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toastTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(toastTimerRef.current), []);

  const showToast = (message: string) => {
    window.clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = window.setTimeout(() => setToast(''), 2600);
  };

  const showUnavailable = (action: string) => {
    showToast(`${action} belum terhubung ke layanan autentikasi.`);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (register && password !== confirmation) {
      showToast('Konfirmasi kata sandi tidak cocok.');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      if (register) await registerAccount(name.trim(), email.trim(), password);
      else await signIn(email.trim(), password);
      window.location.assign('/admin');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Autentikasi gagal.');
    } finally {
      setSubmitting(false);
    }
  };

  const switchPage = (event: MouseEvent<HTMLAnchorElement>, path: '/login' | '/register') => {
    event.preventDefault();
    onNavigate(path);
  };

  return (
    <main className={`auth-page auth-${mode}`}>
      <div className="auth-canvas">
        <form className="auth-card" onSubmit={submit}>
          <header className="auth-header">
            <BrandMark mode={mode} />
            <div className="auth-heading-copy">
              <h1>{register ? 'Buat Akun' : 'Multi-Model AI'}</h1>
              <p>{register ? 'Daftar untuk menyimpan percakapan dan sinkronisasi antar perangkat.' : 'Masuk untuk menyimpan percakapan dan mengakses fitur lengkap.'}</p>
            </div>
          </header>

          <div className="auth-socials">
            <SocialButton icon={register ? 'youtube' : 'circle-x-dark'} light onClick={() => showUnavailable('Google')}>{register ? 'Daftar dengan Google' : 'Lanjutkan dengan Google'}</SocialButton>
            <SocialButton icon="github" onClick={() => showUnavailable('GitHub')}>{register ? 'Daftar dengan GitHub' : 'Lanjutkan dengan GitHub'}</SocialButton>
            <SocialButton icon={register ? 'circle-x-light' : 'app-window'} onClick={() => showUnavailable('Microsoft')}>{register ? 'Daftar dengan Microsoft' : 'Lanjutkan dengan Microsoft'}</SocialButton>
          </div>

          <div className="auth-divider"><span>atau</span></div>

          <div className="auth-fields">
            {register && (
              <div className="auth-field">
                <label htmlFor="name">Nama lengkap</label>
                <input id="name" name="name" type="text" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="John Doe" required />
              </div>
            )}
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nama@email.com" required />
            </div>
            {register ? (
              <>
                <PasswordField id="password" label="Kata sandi" hint="Minimal 8 karakter." value={password} visible={passwordVisible} onChange={setPassword} onToggle={() => setPasswordVisible((current) => !current)} />
                <PasswordField id="password-confirmation" label="Konfirmasi kata sandi" value={confirmation} visible={confirmationVisible} onChange={setConfirmation} onToggle={() => setConfirmationVisible((current) => !current)} />
              </>
            ) : (
              <div className="auth-field">
                <div className="auth-label-row">
                  <label htmlFor="password">Kata sandi</label>
                  <button type="button" onClick={() => showUnavailable('Pemulihan kata sandi')}>Lupa kata sandi?</button>
                </div>
                <span className="auth-password-control">
                  <input id="password" name="password" type={passwordVisible ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••" required />
                  <button type="button" onClick={() => setPasswordVisible((current) => !current)} aria-label={passwordVisible ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}>
                    <AuthIcon name={passwordVisible ? 'eye' : 'eye-off'} className="size-5" />
                  </button>
                </span>
              </div>
            )}
          </div>

          <button type="submit" className="auth-submit interactive" disabled={submitting}>{submitting ? 'Memproses…' : register ? 'Buat Akun' : 'Masuk'}</button>

          <footer className="auth-card-footer">
            <p>
              {register ? 'Sudah punya akun? ' : 'Belum punya akun? '}
              <a href={register ? '/login' : '/register'} onClick={(event) => switchPage(event, register ? '/login' : '/register')}>{register ? 'Masuk' : 'Daftar'}</a>
            </p>
            <p>{register ? 'Dengan mendaftar, kamu menyetujui Ketentuan Layanan dan Kebijakan Privasi.' : 'Dengan masuk, kamu menyetujui Ketentuan Layanan dan Kebijakan Privasi.'}</p>
          </footer>
        </form>

        <p className="auth-watermark">Powered by Cloudflare Workers AI</p>
      </div>
      <Toast message={toast} error />
    </main>
  );
}
